import { action, computed, observable, makeAutoObservable } from 'mobx';
import {
    PromotedEventsState,
    EventCombinedType,
    CompetitionCombinedType,
} from 'src/AppState/CmsState/PromotedEventsState/PromotedEventsState';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { TrpcClient } from 'src/AppState/TrpcClient';

export class PromotedEventState {
    @observable isActive: boolean = this.eventData?.is_active ?? false;
    @observable showDeletePopup: boolean = false;

    constructor(
        private readonly trpcClient: TrpcClient,
        private readonly sdkStaff: SdkStaff,
        private readonly eventData: EventCombinedType,
        private readonly getCompetitionData: (competitionId: number) => CompetitionCombinedType | null,
        private readonly handleDeleteCompetition: (sportSlug: string) => Promise<void>,
        private readonly promotedEventsSectionState: PromotedEventsState
    ) {
        makeAutoObservable(this);
    }

    @computed public get eventName(): string {
        return this.sdkStaff.models.getEvent(this.eventData.event_id)?.name ?? '';
    }

    @computed public get isEventFinished(): boolean | null {
        return this.sdkStaff.models.getEvent(this.eventData.event_id)?.timeMatchStartedAndFinished ?? null;
    }

    @action public setEventActive = async (): Promise<void> => {
        if (this.eventData !== null) {
            const promotedCompetitionDetailsData = {
                ...this.eventData,
                is_active: !this.eventData.is_active,
            };

            this.isActive = !this.isActive;

            try {
                await this.trpcClient.client.promotedEvents.updatePromotedEvent.mutate({
                    id: this.eventData.id,
                    data: promotedCompetitionDetailsData,
                });

                await this.promotedEventsSectionState.promotedEventsResource.refresh();
            } catch (error) {
                console.error(error);
            }
        } else {
            console.error('Cannot find the competition with provided ID.');
        }
    };

    @action handleHideDeletePopup = (): void => {
        this.showDeletePopup = false;
    };

    @action handleShowDeletePopup = (): void => {
        this.showDeletePopup = true;
    };

    @action public handleDeleteEvent = async (): Promise<void> => {
        if (this.eventData !== null) {
            this.promotedEventsSectionState.setIsLoading(true);
            const competitionId = this.eventData.competition_id;
            const removeResult = await this.trpcClient.client.promotedEvents.deletePromotedEvent.mutate({
                id: this.eventData.id,
            });

            if (removeResult.status === 200) {
                await this.promotedEventsSectionState.promotedEventsResource.refresh();
                this.promotedEventsSectionState.setSportsDataFromResource();

                const eventsList = this.getCompetitionData(competitionId)?.events_list;

                if (eventsList?.length === 0) {
                    await this.handleDeleteCompetition(this.eventData.sport_id);
                }
            } else {
                console.error("The item couldn't be deleted.");
            }
            this.promotedEventsSectionState.setIsLoading(false);
        }
    };
}
